import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { ConditionsCommercialesFournisseur } from "@/composants/conditions-commerciales";
import { FormulaireFournisseur } from "@/composants/formulaire-fournisseur";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampSelection } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Donnee, Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import { STATUTS_FOURNISSEUR, statutFournisseur, tonStatutFournisseur } from "@/lib/libelles";
import { api, type FicheFournisseur, type StatutFournisseur } from "@/lib/api";
import { useSession } from "@/session";

const OPTIONS_STATUT = Object.entries(STATUTS_FOURNISSEUR).map(([valeur, libelle]) => ({
  valeur,
  libelle,
}));

/** La fiche d'un fournisseur : ses coordonnées, son statut et ses contacts. */
export function PageFournisseur() {
  const { id = "" } = useParams();
  const session = useSession();
  const peutGerer = session.peut("GERER_COMMANDE_FOURNISSEUR");
  const voitLesNotesConfidentielles = session.peut("CONSULTER_NOTES_CONFIDENTIELLES_FOURNISSEURS");
  // Ticket #76 : le seuil et les frais sont des montants ; la section ne s'ouvre pas sans eux.
  const voitLesMontants = session.peut("CONSULTER_MONTANTS");

  const { donnees, erreur, enCours, recharger } = useChargement<FicheFournisseur>(
    (signal) => api.fournisseur(id, signal),
    [id],
  );
  const [modifiee, setModifiee] = useState<FicheFournisseur | null>(null);
  const [correction, setCorrection] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);

  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!donnees || enCours) return <Chargement message="Chargement du fournisseur…" />;

  const fiche = modifiee && modifiee.id === id ? modifiee : donnees;
  const agir = async (geste: () => Promise<FicheFournisseur>) => {
    setRefus(null);
    try {
      setModifiee(await geste());
      return true;
    } catch (cause) {
      setRefus(messageDe(cause));
      return false;
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/fournisseurs"
        className="inline-flex items-center gap-1.5 text-sm text-encre-douce hover:text-encre"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Tous les fournisseurs
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="min-w-0 break-words text-xl font-semibold text-encre">{fiche.nom}</h1>
        <Etiquette ton={tonStatutFournisseur(fiche.statut)}>
          {statutFournisseur(fiche.statut)}
        </Etiquette>
      </div>

      {refus ? <Erreur message={refus} /> : null}

      <Carte>
        <EnTeteCarte className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-encre">Coordonnées</h2>
          {peutGerer ? (
            <Bouton taille="compact" variante="discret" onClick={() => setCorrection((o) => !o)}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              {correction ? "Fermer" : "Corriger"}
            </Bouton>
          ) : null}
        </EnTeteCarte>
        <CorpsCarte>
          {correction ? (
            <FormulaireFournisseur
              fournisseur={fiche}
              avecNotesConfidentielles={voitLesNotesConfidentielles}
              libelleValider="Enregistrer"
              onAnnuler={() => setCorrection(false)}
              onValider={async (champs) => {
                if (await agir(() => api.corrigerFournisseur(fiche.id, champs))) {
                  setCorrection(false);
                }
              }}
            />
          ) : (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3">
              <Donnee libelle="SIREN">{fiche.siren ?? "—"}</Donnee>
              <Donnee libelle="SIRET">{fiche.siret ?? "—"}</Donnee>
              <Donnee libelle="Catégorie">{fiche.categorie_libelle ?? "—"}</Donnee>
              <Donnee libelle="Délai moyen">
                {fiche.delai_moyen_jours === null
                  ? "—"
                  : `${fiche.delai_moyen_jours} jour${fiche.delai_moyen_jours > 1 ? "s" : ""}`}
              </Donnee>
              <Donnee libelle="Adresse">
                {fiche.adresse_ligne1 ?? "—"}
                {fiche.code_postal || fiche.ville ? <br /> : null}
                {[fiche.code_postal, fiche.ville].filter(Boolean).join(" ")}
              </Donnee>
              <Donnee libelle="Adresse électronique">
                <span className="break-all">{fiche.email ?? "—"}</span>
              </Donnee>
              <Donnee libelle="Téléphone">{fiche.telephone ?? "—"}</Donnee>
              {fiche.notes_generales ? (
                <div className="col-span-full">
                  <Donnee libelle="Notes générales">
                    <span className="whitespace-pre-line">{fiche.notes_generales}</span>
                  </Donnee>
                </div>
              ) : null}
              {fiche.notes_confidentielles ? (
                <div className="col-span-full">
                  <Donnee libelle="Notes confidentielles">
                    <span className="whitespace-pre-line">{fiche.notes_confidentielles}</span>
                  </Donnee>
                </div>
              ) : null}
            </dl>
          )}
        </CorpsCarte>
      </Carte>

      {peutGerer ? (
        <ChangerStatut
          statut={fiche.statut}
          onChanger={(statut) => agir(() => api.changerStatutFournisseur(fiche.id, statut))}
        />
      ) : null}

      {voitLesMontants ? <ConditionsCommercialesFournisseur fournisseurId={fiche.id} /> : null}

      <Carte>
        <EnTeteCarte>
          <h2 className="text-sm font-semibold text-encre">Contacts</h2>
        </EnTeteCarte>
        {fiche.contacts.length === 0 ? (
          <Vide titre="Aucun contact" />
        ) : (
          <ul className="divide-y divide-filet">
            {fiche.contacts.map((contact) => (
              <li key={contact.id} className="flex items-start gap-3 px-4 py-3">
                <div className="min-w-0 flex-1 text-sm">
                  <p className="font-medium text-encre">
                    {[contact.prenom, contact.nom].filter(Boolean).join(" ")}
                    {contact.fonction ? (
                      <span className="font-normal text-encre-douce"> — {contact.fonction}</span>
                    ) : null}
                  </p>
                  <p className="break-all text-encre-douce">
                    {[contact.email, contact.telephone].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                {peutGerer ? (
                  <Bouton
                    taille="compact"
                    variante="discret"
                    aria-label={`Retirer ${contact.nom}`}
                    onClick={() =>
                      agir(() => api.retirerContactFournisseur(fiche.id, contact.id))
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    Retirer
                  </Bouton>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {peutGerer ? (
          <CorpsCarte className="border-t border-filet">
            <AjoutContact
              onAjouter={(contact) => agir(() => api.ajouterContactFournisseur(fiche.id, contact))}
            />
          </CorpsCarte>
        ) : null}
      </Carte>
    </div>
  );
}

function ChangerStatut({
  statut,
  onChanger,
}: {
  statut: StatutFournisseur;
  onChanger: (statut: StatutFournisseur) => Promise<boolean>;
}) {
  const [choisi, setChoisi] = useState<StatutFournisseur>(statut);
  const [envoi, setEnvoi] = useState(false);
  return (
    <Carte>
      <EnTeteCarte>
        <h2 className="text-sm font-semibold text-encre">Statut</h2>
      </EnTeteCarte>
      <CorpsCarte>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={async (evenement) => {
            evenement.preventDefault();
            setEnvoi(true);
            await onChanger(choisi);
            setEnvoi(false);
          }}
        >
          <ChampSelection
            libelle="Statut du fournisseur"
            aide="On ne commande que chez un fournisseur actif ; les commandes passées restent suivies."
            options={OPTIONS_STATUT}
            value={choisi}
            onChange={(e) => setChoisi(e.target.value as StatutFournisseur)}
          />
          <Bouton type="submit" disabled={envoi || choisi === statut}>
            Changer le statut
          </Bouton>
        </form>
      </CorpsCarte>
    </Carte>
  );
}

const CONTACT_VIDE = { nom: "", prenom: "", fonction: "", email: "", telephone: "" };

function AjoutContact({
  onAjouter,
}: {
  onAjouter: (contact: Record<string, string | null>) => Promise<boolean>;
}) {
  const [valeurs, setValeurs] = useState(CONTACT_VIDE);
  const [envoi, setEnvoi] = useState(false);
  const poser = (champ: keyof typeof CONTACT_VIDE) => (valeur: string) =>
    setValeurs({ ...valeurs, [champ]: valeur });

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        const contact = Object.fromEntries(
          Object.entries(valeurs).map(([cle, valeur]) => [cle, valeur.trim() || null]),
        );
        if (await onAjouter(contact)) setValeurs(CONTACT_VIDE);
        setEnvoi(false);
      }}
    >
      <h3 className="text-sm font-medium text-encre">Ajouter un contact</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ
          libelle="Nom du contact"
          required
          value={valeurs.nom}
          onChange={(e) => poser("nom")(e.target.value)}
        />
        <Champ
          libelle="Prénom du contact"
          value={valeurs.prenom}
          onChange={(e) => poser("prenom")(e.target.value)}
        />
        <Champ
          libelle="Fonction"
          value={valeurs.fonction}
          onChange={(e) => poser("fonction")(e.target.value)}
        />
        <Champ
          libelle="Adresse électronique du contact"
          type="email"
          value={valeurs.email}
          onChange={(e) => poser("email")(e.target.value)}
        />
        <Champ
          libelle="Téléphone du contact"
          type="tel"
          value={valeurs.telephone}
          onChange={(e) => poser("telephone")(e.target.value)}
        />
      </div>
      <Bouton type="submit" className="self-start" disabled={envoi}>
        Ajouter le contact
      </Bouton>
    </form>
  );
}
